import { useMsal } from '@azure/msal-react';
import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { useApi } from './useApi';
import { User, UserRole } from '../types';

interface AuthContextType {
    user: User | null;
    loading: boolean;
    isAuthenticated: boolean;
    isAdmin: boolean;
    isPM: boolean;
    isOwner: boolean;
    isExecutive: boolean;
    canAccessPage: (page: string) => boolean;
    canPerformAction: (action: 'create' | 'read' | 'update' | 'delete', resource: string) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
    const { accounts, inProgress } = useMsal();
    const api = useApi();
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function loadUser() {
            if (accounts.length > 0 && inProgress === 'none') {
                try {
                    const userData = await api.getCurrentUser();
                    setUser(userData);
                } catch (error) {
                    console.error('Failed to load user:', error);
                    setUser(null);
                }
            } else if (accounts.length === 0) {
                setUser(null);
            }
            setLoading(false);
        }

        loadUser();
    }, [accounts, inProgress, api]); // Added api dependency but useApi returns memoized object

    const isAuthenticated = accounts.length > 0;
    const isAdmin = user?.role === 'admin';
    const isPM = user?.role === 'pm';
    const isOwner = user?.role === 'owner';
    const isExecutive = user?.role === 'executive';

    // Check if user can access a specific page
    const canAccessPage = (page: string): boolean => {
        if (!user) return false;
        if (isAdmin) return true;

        const role = user.role || 'normal';

        const pageAccess: Record<UserRole, Record<string, boolean>> = {
            admin: { projects: true, list: true, kanban: true, roadmap: true, matrix: true, dashboard: true, executive: true, 'admin-users': true, 'admin-audit': true },
            pm: { projects: true, list: true, kanban: true, roadmap: true, matrix: true, dashboard: true, executive: true },
            owner: { projects: false, list: true, kanban: true, roadmap: true, matrix: false, dashboard: true, executive: false },
            executive: { projects: false, list: false, kanban: false, roadmap: false, matrix: true, dashboard: true, executive: true },
            normal: user.customPageAccess || { projects: false, list: false, kanban: false, roadmap: false, matrix: false, dashboard: false, executive: false },
        };

        return pageAccess[role as UserRole]?.[page] ?? false;
    };

    // Check if user can perform action on resource
    const canPerformAction = (action: 'create' | 'read' | 'update' | 'delete', resource: string): boolean => {
        if (!user) return false;
        if (isAdmin) return true;

        const role = user.role || 'normal';

        // Resource-specific permissions per role
        // Format: { role: { resource: { action: boolean } } }
        const resourcePermissions: Record<UserRole, Record<string, Record<string, boolean>>> = {
            admin: {
                project: { create: true, read: true, update: true, delete: true },
                epic: { create: true, read: true, update: true, delete: true },
                session: { create: true, read: true, update: true, delete: true },
                user: { create: true, read: true, update: true, delete: true },
            },
            pm: {
                project: { create: false, read: true, update: true, delete: false },
                epic: { create: true, read: true, update: true, delete: false },
                session: { create: true, read: true, update: true, delete: false },
                user: { create: false, read: true, update: false, delete: false },
            },
            owner: {
                project: { create: false, read: true, update: false, delete: false },
                epic: { create: false, read: true, update: true, delete: false },
                session: { create: true, read: true, update: false, delete: false },
                user: { create: false, read: true, update: false, delete: false },
            },
            executive: {
                project: { create: false, read: true, update: false, delete: false },
                epic: { create: false, read: true, update: false, delete: false },
                session: { create: false, read: true, update: false, delete: false },
                user: { create: false, read: true, update: false, delete: false },
            },
            normal: {
                // Normal users can only read epics and sessions
                project: { create: false, read: false, update: false, delete: false },
                epic: { create: false, read: true, update: false, delete: false },
                session: { create: false, read: true, update: false, delete: false },
                user: { create: false, read: false, update: false, delete: false },
            },
        };

        return resourcePermissions[role as UserRole]?.[resource]?.[action] ?? false;
    };

    return (
        <AuthContext.Provider value={{
            user,
            loading,
            isAuthenticated,
            isAdmin,
            isPM,
            isOwner,
            isExecutive,
            canAccessPage,
            canPerformAction,
        }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within AuthProvider');
    }
    return context;
}
